"use client";

import * as React from "react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js/min";

import { type InputVariantProps, inputVariants } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";

type PhoneInputProps = Omit<
  React.ComponentProps<"input">,
  "onChange" | "value" | "ref" | "size"
> &
  InputVariantProps &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange?: (value: RPNInput.Value) => void;
    countrySelectClassName?: string;
    suffix?: React.ReactNode;
    "data-testid"?: string;
  };

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
  React.forwardRef<React.ElementRef<typeof RPNInput.default>, PhoneInputProps>(
    (
      {
        className,
        onChange,
        size,
        variant,
        value,
        countrySelectClassName,
        suffix,
        "data-testid": dataTestId,
        numberInputProps: numberInputPropsProp,
        ...restProps
      },
      ref,
    ) => {
      const parsed = React.useMemo(() => {
        if (!value) return null;
        return parsePhoneNumberFromString(String(value));
      }, [value]);

      const showValidationState =
        !suffix && !!value && String(value).length > 3;
      const isValid = value ? (parsed?.isValid() ?? false) : null;
      const mergedNumberInputProps =
        numberInputPropsProp as React.ComponentPropsWithoutRef<"input"> | undefined;

      return (
        <div
          className={cn(
            inputVariants({ size, variant }),
            "group/phone relative flex w-full items-stretch overflow-hidden p-0",
          )}
        >
          <RPNInput.default
            ref={ref}
            className="flex w-full min-w-0 flex-1"
            flagComponent={FlagComponent}
            countrySelectComponent={CountrySelect}
            smartCaret={false}
            value={value || undefined}
            countrySelectProps={{
              size,
              className: countrySelectClassName,
            }}
            numberInputProps={{
              ...mergedNumberInputProps,
              className: cn(
                "h-full min-w-0 w-full flex-1 border-0 bg-transparent pl-2.5 shadow-none",
                "outline-none focus:outline-none focus:ring-0",
                "focus-visible:outline-none focus-visible:ring-0",
                suffix ? "pr-16" : "pr-9",
                className,
                mergedNumberInputProps?.className,
              ),
              ...(dataTestId ? { "data-testid": dataTestId } : {}),
            }}
            onChange={(nextValue) =>
              onChange?.(nextValue || ("" as RPNInput.Value))
            }
            {...restProps}
          />

          {suffix && (
            <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
              {suffix}
            </div>
          )}

          {showValidationState && isValid !== null && (
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              {isValid ? (
                <div className="flex size-4 items-center justify-center rounded-full bg-emerald-500/20">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M2 5L4 7L8 3"
                      stroke="#34d399"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              ) : (
                <div className="flex size-4 items-center justify-center rounded-full bg-destructive/20">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M3 3L7 7M7 3L3 7"
                      stroke="#f87171"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </div>
          )}
        </div>
      );
    },
  );
PhoneInput.displayName = "PhoneInput";
export { PhoneInput };

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country: RPNInput.Country) => void;
  className?: string;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
  className,
}: CountrySelectProps & InputVariantProps) => {
  const [searchValue, setSearchValue] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const filteredCountries = React.useMemo(() => {
    return countryList.filter(({ label }) =>
      label.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [countryList, searchValue]);

  return (
    <Select.Root
      open={isOpen}
      modal
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) {
          setSearchValue("");
          requestAnimationFrame(() => searchRef.current?.focus());
        }
      }}
    >
      <Select.Trigger
        size="none"
        variant="none"
        disabled={disabled}
        className={cn(
          "h-full w-fit shrink-0 cursor-pointer gap-1 rounded-none border-0 border-r border-contrast/10 bg-transparent px-2.5 shadow-none",
          "transition-colors hover:bg-contrast/4 active:bg-contrast/6 data-[state=open]:bg-contrast/6",
          "focus-visible:ring-0 focus-visible:outline-none",
          className,
        )}
      >
        <FlagComponent
          country={selectedCountry}
          countryName={selectedCountry}
        />
        <Select.Icon
          size={14}
          className={cn(
            "ml-0 opacity-50 transition-transform duration-300",
            disabled && "hidden",
            isOpen && "rotate-180",
          )}
        />
      </Select.Trigger>

      <Select.Content
        align="end"
        matchTriggerWidth={false}
        className="w-[300px]"
      >
        <Select.Search
          ref={searchRef}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search country..."
        />

        <Select.List className="max-h-72">
          {filteredCountries.length === 0 ? (
            <div className="px-3 py-2 text-sm text-contrast/40">
              No country found.
            </div>
          ) : (
            filteredCountries.map(({ value: country, label: countryName }) =>
              country ? (
                <Select.Item
                  key={country}
                  selected={country === selectedCountry}
                  onClick={() => {
                    onChange(country);
                    setIsOpen(false);
                  }}
                >
                  <FlagComponent country={country} countryName={countryName} />
                  <span className="flex-1 text-left text-sm">
                    {countryName}
                  </span>
                  <span className="text-sm text-foreground/50">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
                  <Icon
                    name="IconCheckmark1"
                    size={14}
                    className={cn(
                      "size-4 text-heading",
                      country === selectedCountry ? "opacity-100" : "opacity-0",
                    )}
                  />
                </Select.Item>
              ) : null,
            )
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = country ? flags[country] : undefined;

  if (!country || !Flag) {
    return (
      <span className="h-3.5 w-5 shrink-0 rounded-[3px] bg-contrast/10 ring-1 ring-inset ring-contrast/15" />
    );
  }

  // Full rectangular flag (the source SVGs are 3:2). A circular crop hid most
  // of the flag and made countries hard to tell apart.
  return (
    <span className="relative flex h-3.5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-[3px] ring-1 ring-inset ring-contrast/15 [&_svg:not([class*='size-'])]:absolute [&_svg:not([class*='size-'])]:inset-0 [&_svg:not([class*='size-'])]:size-full">
      <Flag title={countryName} />
    </span>
  );
};

export function getFullPhoneNumber(
  countryCode: CountryCode,
  phoneNumber: string,
): string {
  if (!phoneNumber) return "";
  if (phoneNumber.startsWith("+")) return phoneNumber;
  const parsed = parsePhoneNumberFromString(phoneNumber, countryCode);
  if (parsed) return parsed.format("E.164");
  const digits = phoneNumber.replace(/\D/g, "");
  const dialCode = `+${getCountryCallingCode(countryCode)}`;
  return `${dialCode}${digits}`;
}
